import { Loader2, Plus, User } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MetricsTotalizers } from './components/MetricsTotalizers';
import { OnBoardingTable } from './components/OnBoardingTable';

import { NoContentFallback } from '@/components/NoContentFallBack';
import { Pagination } from '@/components/Pagination';
import { SearchCustomersForm } from '@/components/SearchCustomers';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ROUTE_PATHS } from '@/config/constants';
import { useGetPaginatedCustomers } from '@/hooks/useGetPaginatedCustomers';
interface Filters {
  searchTerm: string | null;
  platform: string | null;
  status: boolean | null;
}

export function AdminOnBoarding() {
  const navigate = useNavigate();
  const [search, setSearch] = useState<Partial<Filters>>();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { customers, totalPages, isLoading } = useGetPaginatedCustomers({
    page: page,
    limit: perPage,
    status: true,
    ...(search?.searchTerm !== null && { searchTerm: search?.searchTerm }),
  });

  return (
    <div className="min-h-screen bg-gray-100">
      <Card>
        <CardHeader>
          <div className="items-left flex flex-col justify-between px-2">
            <h1 className="text-3xl font-bold">Onboarding Dashboard</h1>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <>
            <main className="flex flex-1 flex-col p-2">
              <MetricsTotalizers />
              <div className="mb-8 mt-8 flex flex-row items-center justify-end gap-4">
                <div className="w-1/8 hidden">
                  <Select
                    value={perPage.toString()}
                    onValueChange={(value) => {
                      setPerPage(Number(value));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Items per page" />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 20, 50].map((n) => (
                        <SelectItem key={n} value={n.toString()}>
                          {n} per page
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-row justify-end gap-4">
                  <SearchCustomersForm
                    onPageChange={setPage}
                    onSearchChange={setSearch}
                  />
                  <Button
                    size="sm"
                    className="bg-blue-600 text-white hover:bg-blue-700"
                    onClick={() => navigate(ROUTE_PATHS.ADMIN_CUSTOMERS_CREATE)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    New Client
                  </Button>
                </div>
              </div>
              {customers.length > 0 && !isLoading && (
                <>
                  <OnBoardingTable customers={customers} perPage={perPage} />
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    isLoading={isLoading}
                  />
                </>
              )}

              {isLoading && (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}

              {!customers.length && !isLoading && (
                <NoContentFallback
                  description="You have not onboarded any customers with this name or ID yet."
                  title="No customers found"
                  icon={User}
                />
              )}
            </main>
          </>
        </CardContent>
      </Card>
    </div>
  );
}
